import {
	GuardedType,
	isArray,
	isBoolean,
	IsInterface,
	isNullable,
	isSingletonStringUnion,
	isString
} from "generic-type-guard";

import { EntityInstancePath } from "@com.mgmtp.a12.kernel/kernel-md-facade";
import { ModelPath } from "@com.mgmtp.a12.base/base-model-api/lib/main/model";
import { Attachment } from "@com.mgmtp.a12.dataservices/dataservices-access";

export const isSortierungItem = new IsInterface()
	.withProperties({
		id: isString
	})
	.withOptionalProperties({
		label: isString
	})
	.get();
export type SortierungItem = GuardedType<typeof isSortierungItem>;

export const isAttachmentItem = new IsInterface()
	.withProperties({
		value: isString
	})
	.get();
export type AttachmentItem = GuardedType<typeof isAttachmentItem>;

export const isEnumCacheOption = new IsInterface()
	.withProperties({
		value: isString,
		id: isString
	})
	.get();
export type EnumCacheOption = GuardedType<typeof isEnumCacheOption>;

export const isExternalEnumCacheItem = new IsInterface()
	.withProperties({
		key: isSingletonStringUnion(
			"archive_attachment_options",
			"archive_initial_attachment_options",
			"archive_dokart_options",
			"archive_papierform_options",
			"ARCHIVE_SENDER"
		)
	})
	.withOptionalProperties({
		options: isArray(isEnumCacheOption)
	})
	.get();
export type ExternalEnumCacheItem = GuardedType<typeof isExternalEnumCacheItem>;

export const isArchiveModalMetadata = new IsInterface()
	.withOptionalProperties({
		jobUuid: isString,
		containsInitialAttachments: isBoolean,
		containsAttachments: isBoolean,
		containsGeneratedDocuments: isBoolean,
		allowCustomSegmentSorting: isBoolean,
		forceInitialAttachments: isBoolean,
		busy: isBoolean,
		cancelDisabled: isBoolean,
		status: isSingletonStringUnion("warning", "info", "success", "error"),
		excludedAttachments: isString,
		excludedMsgAttachments: isString,
		duplicatedItemTexts: isString,
		archivePreviewUrl: isString,
		previewFileName: isString,
		message: isString
	})
	.get();
export type ArchiveModalMetadata = GuardedType<typeof isArchiveModalMetadata>;

export const isArchiveModalDocument = new IsInterface()
	.withProperty(
		"root",
		new IsInterface()
			.withOptionalProperties({
				aktenkennzeichen: isNullable(isString),
				betreff: isNullable(isString),
				absender: isNullable(isString),
				dokumentenArt: isNullable(isString),
				papierform: isNullable(isString),
				sortierungGeneratedDocuments: isNullable(isArray(isSortierungItem)),
				sortierungInitialAttachments: isNullable(isArray(isSortierungItem)),
				sortierungAttachments: isNullable(isArray(isSortierungItem)),
				sortierungSegments: isNullable(isArray(isSortierungItem)),
				mappenNummer: isNullable(isString),
				bezug: isNullable(isString),
				attachments: isNullable(isArray(isAttachmentItem)),
				initialAttachments: isNullable(isArray(isAttachmentItem)),
				externalEnumCache: isArray(isExternalEnumCacheItem),
				metadata: isArchiveModalMetadata
			})
			.get()
	)
	.get();
export type ArchiveModalDocument = GuardedType<typeof isArchiveModalDocument>;

export const isArchiveModalDocumentData = new IsInterface().withProperty("document", isArchiveModalDocument).get();
export type ArchiveModalDocumentData = GuardedType<typeof isArchiveModalDocumentData>;

export type Configuration = {
	requireAllApplicationAttachments: boolean;
	sorting: string[];
	isCompound: boolean;
	allowCustomSegmentSorting: boolean;
	initialAttachmentsSelection: "ALL" | "PRESELECTED" | "UNSELECTED";
	defaultAktenzeichen?: string;
};

export type AttachmentInstancePath = {
	path: EntityInstancePath;
	descriptor: AttachmentPathDescriptor;
};

export type AttachmentPathDescriptor = {
	modelPath: ModelPath;
	type: AttachmentType;
};
export type AttachmentType = "GENERIERT" | "ATTACHMENT" | "INITIAL";

export type TypedAttachment = {
	attachment: Attachment;
	type: AttachmentType;
	initiallyAttached: boolean;
};

export const isEldoradoMetadataResultDto = new IsInterface()
	.withOptionalProperties({
		text: isString,
		alias: isString
	})
	.get();
export type EldoradoMetadataResultDto = GuardedType<typeof isEldoradoMetadataResultDto>;

export const isEldoradoMetadataWithContextDto = new IsInterface()
	.withOptionalProperties({
		docArtValues: isArray(isEldoradoMetadataResultDto),
		betreff: isString,
		bezug: isString,
		absenderValues: isArray(isEldoradoMetadataResultDto),
		allowedAttachmentExtensions: isArray(isString),
		isPolizei: isBoolean,
		duplicatedItemTexts: isArray(isString)
	})
	.get();
export type EldoradoMetadataWithContextDto = GuardedType<typeof isEldoradoMetadataWithContextDto>;
